// /////////////////////////////////////////////////////////////
// [ファイル] AssistantThinkingProvider.tsx
// [概要] AI アシスタントの「推論中」状態を同一画面内の演出部品へ伝える極小ハブ。
//        WorkspaceAssistantPanel が announce で現在値を放送し、購読者
//        (背景のコア演出など) が subscribe で受け取る。
//        ・保持するのは真偽値1個と購読者の一覧のみ (履歴は溜めない)
//        ・React state を使わないため、値の切替で再レンダリングは発生しない
//        ・Provider が無い画面では既定の no-op に流れ、何も起きない
//          (呼び出し側にスイッチ類の設定を要求しない構成)
// /////////////////////////////////////////////////////////////
import React, { createContext, useContext, useRef } from 'react';

type Listener = (thinking: boolean) => void;

export interface AssistantThinkingHub {
  // 現在の推論中フラグを放送する (同値の再放送は無視)
  announce: (thinking: boolean) => void;
  // 購読を登録し、解除関数を返す。登録直後に現在値が一度通知される
  subscribe: (listener: Listener) => () => void;
}

// Provider が無い画面用の既定ハブ (何もしない)
const noopHub: AssistantThinkingHub = {
  announce: () => {},
  subscribe: () => () => {},
};

const AssistantThinkingContext = createContext<AssistantThinkingHub>(noopHub);

export function AssistantThinkingProvider({ children }: { children: React.ReactNode }) {
  // ハブは初回マウント時に1度だけ生成し、以後は同一参照を配り続ける
  const hubRef = useRef<AssistantThinkingHub | null>(null);
  if (!hubRef.current) {
    const listeners = new Set<Listener>();
    let current = false;
    hubRef.current = {
      announce: (thinking) => {
        if (thinking === current) return;
        current = thinking;
        listeners.forEach((listener) => listener(thinking));
      },
      subscribe: (listener) => {
        listeners.add(listener);
        listener(current);
        return () => {
          listeners.delete(listener);
        };
      },
    };
  }
  return (
    <AssistantThinkingContext.Provider value={hubRef.current}>
      {children}
    </AssistantThinkingContext.Provider>
  );
}

export function useAssistantThinking(): AssistantThinkingHub {
  return useContext(AssistantThinkingContext);
}
